import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { Button, Title } from "lola-framework-ui-test";
import { FloatingField, type FloatingFieldColors } from "../FloatingField";
import { useStore } from "../../store/globalState";
import {
  PayoutMethodType,
  type EstimateOption,
  type FundRequestPayoutInput,
} from "../../interfaces/payout";
import {
  mockCatalogFor,
  requestCashPickupHabits,
} from "../../gallery/requestMock";
import {
  bankPrefixMismatch,
  inspectBankAccountNumber,
} from "../../utils/bankAccountNumber";
import {
  CARDHOLDER_NAME_MAX_LENGTH,
  formatCardholderNameInput,
  isValidCardholderName,
} from "../../utils/cardholderName";
import {
  catalogNamedPayer,
  dedupeOfferableCashPayers,
  dedupeOfferableCashPayersForState,
  isWildcardStateCapability,
  payerCodesEquivalent,
  resolveCashPickupOption,
} from "../../utils/payerCode";
import { BackArrowLight, CloseLight } from "../../assets/navIcons";

const MONO_INPUT: CSSProperties = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  letterSpacing: "0.04em",
};

function payoutFieldColors(view: {
  background: string;
  title: string;
  bodyCopy: string;
  subtitile: string;
  errorColor?: string;
}): FloatingFieldColors {
  return {
    background: view.background,
    title: view.title,
    bodyCopy: view.bodyCopy,
    inactiveColor: view.subtitile,
    errorColor: view.errorColor,
  };
}

export interface PayoutDetailsResult {
  payout: FundRequestPayoutInput;
  /** Pre-quote option the selection priced against, when one matched. */
  option: EstimateOption | null;
}

export interface PayoutDetailsSlotProps {
  method: PayoutMethodType;
  destinationCountry: string;
  /** Pre-quote options for the corridor — drives the priced SKU. */
  options: EstimateOption[];
  value?: FundRequestPayoutInput | null;
  habitsBusy?: boolean;
  /** Control Dock: omit saved chips. */
  habitsEmpty?: boolean;
  onBack: () => void;
  onClose: () => void;
  onSubmit: (result: PayoutDetailsResult) => void;
}

/** Local account length expected per destination country (digits only). */
const ACCOUNT_LENGTH: Record<string, number> = { MX: 18, AR: 22, PE: 20 };
const MAX_ACCOUNT_NUMBER_LENGTH = 34;

const payerCodeOf = (
  method: PayoutMethodType,
  payout: FundRequestPayoutInput,
): string | null => {
  switch (method) {
    case PayoutMethodType.BANK_ACCOUNT:
      return payout.bankId ?? null;
    case PayoutMethodType.CASH_PICKUP:
      return payout.payerId ?? null;
    case PayoutMethodType.MOBILE_WALLET:
      return payout.carrierId ?? null;
    default:
      return null;
  }
};

const resolveOption = (
  method: PayoutMethodType,
  payout: FundRequestPayoutInput,
  options: EstimateOption[],
): EstimateOption | null => {
  const forMethod = options.filter((o) => o.payoutMethod === method);
  const payerCode = payerCodeOf(method, payout);
  if (method === PayoutMethodType.CASH_PICKUP) {
    if (!payerCode) return null;
    return resolveCashPickupOption(options, payerCode, payout.stateCode);
  }
  if (payerCode) {
    const byPayer = forMethod.find((o) =>
      payerCodesEquivalent(o.payerCode, payerCode),
    );
    if (byPayer) return byPayer;
    return null;
  }
  return forMethod[0] ?? null;
};

/**
 * Gallery-safe second stage of the payout picker. Mirrors the production
 * `PayoutDetailsSlot` UI (bank / cash / card / wallet sub-forms) but sources its
 * bank / state / payer / carrier lists from the static `mockCatalogFor` data
 * instead of the live `/widget/catalog/*` endpoints.
 */
export const PayoutDetailsSlot: React.FC<PayoutDetailsSlotProps> = ({
  method,
  destinationCountry,
  options,
  value,
  habitsBusy = false,
  habitsEmpty = false,
  onBack,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation("request");
  const { theme } = useStore();
  const { bodyCopy } = theme.whiteView;

  const submit = (payout: FundRequestPayoutInput): void => {
    onSubmit({ payout, option: resolveOption(method, payout, options) });
  };

  return (
    <section className="payout-details-slot">
      <header className="payout-details-header">
        <button
          type="button"
          className="payout-header-action payout-back-btn"
          aria-label={t("common.back")}
          onClick={onBack}
        >
          <BackArrowLight size={36} colors={[bodyCopy, bodyCopy]} />
        </button>
        <Title
          title={t(`payout.details.${method}.title`)}
          align="center"
          color={bodyCopy}
        />
        <button
          type="button"
          className="payout-header-action payout-close-btn"
          aria-label="Close"
          onClick={onClose}
        >
          <CloseLight size={36} colors={[bodyCopy, bodyCopy]} />
        </button>
      </header>

      {method === PayoutMethodType.BANK_ACCOUNT && (
        <BankAccountForm
          destinationCountry={destinationCountry}
          options={options}
          value={value}
          onSubmit={submit}
        />
      )}
      {method === PayoutMethodType.CASH_PICKUP && (
        <CashPickupForm
          destinationCountry={destinationCountry}
          options={options}
          value={value}
          habitsBusy={habitsBusy}
          habitsEmpty={habitsEmpty}
          onSubmit={submit}
        />
      )}
      {method === PayoutMethodType.PUSH_TO_CARD && (
        <CardForm value={value} onSubmit={submit} />
      )}
      {method === PayoutMethodType.MOBILE_WALLET && (
        <MobileWalletForm
          destinationCountry={destinationCountry}
          value={value}
          onSubmit={submit}
        />
      )}
    </section>
  );
};

// ---------------------------------------------------------------------------
// Sub-forms
// ---------------------------------------------------------------------------

const groupDigits = (digits: string): string =>
  digits.replace(/(\d{4})(?=\d)/g, "$1 ");

const formatCardExpiry = (raw: string): string => {
  const digits = raw.replace(/\D/g, "").slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

const isExpiryValid = (expiry: string): boolean => {
  const match = expiry.match(/^(\d{2})\/(\d{2})$/);
  if (!match) return false;
  const month = Number(match[1]);
  return month >= 1 && month <= 12;
};

interface CardFormProps {
  value?: FundRequestPayoutInput | null;
  onSubmit: (payout: FundRequestPayoutInput) => void;
}

const CardForm: React.FC<CardFormProps> = ({ value, onSubmit }) => {
  const { t } = useTranslation("request");
  const { theme } = useStore();
  const { backgroundBtn, textColorBtn, bodyCopy } = theme.whiteView;
  const fieldColors = payoutFieldColors(theme.whiteView);
  const [pan, setPan] = useState(value?.cardPan ?? "");
  const [expiry, setExpiry] = useState(value?.cardExpiry ?? "");
  const [cardholderName, setCardholderName] = useState(
    formatCardholderNameInput(value?.cardholderName ?? ""),
  );

  const digits = pan.replace(/\D/g, "");
  const valid =
    digits.length >= 13 &&
    isExpiryValid(expiry) &&
    isValidCardholderName(cardholderName);

  return (
    <form
      className="payout-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid) return;
        onSubmit({
          source: "new",
          cardPan: digits,
          cardExpiry: expiry,
          cardholderName: cardholderName.trim(),
          last4: digits.slice(-4),
        });
      }}
    >
      <div className="payout-field">
        <FloatingField
          label={t("payout.fields.cardholderName")}
          value={cardholderName}
          required
          colors={fieldColors}
        >
          <input
            type="text"
            style={{ color: bodyCopy }}
            value={cardholderName}
            maxLength={CARDHOLDER_NAME_MAX_LENGTH}
            autoComplete="cc-name"
            autoCapitalize="characters"
            spellCheck={false}
            onChange={(e) =>
              setCardholderName(formatCardholderNameInput(e.target.value))
            }
          />
        </FloatingField>
      </div>

      <div className="payout-field">
        <FloatingField
          label={t("payout.fields.cardPan")}
          placeholder={t("payout.fields.cardPanPlaceholder")}
          value={groupDigits(digits)}
          required
          colors={fieldColors}
        >
          <input
            type="text"
            inputMode="numeric"
            autoComplete="cc-number"
            style={{ color: bodyCopy, ...MONO_INPUT }}
            value={groupDigits(digits)}
            onChange={(e) => setPan(sanitizeDigits(e.target.value, 19))}
          />
        </FloatingField>
      </div>

      <div className="payout-field">
        <FloatingField
          label={t("payout.fields.cardExpiry")}
          placeholder={t("payout.fields.cardExpiryPlaceholder")}
          value={expiry}
          required
          colors={fieldColors}
        >
          <input
            type="text"
            inputMode="numeric"
            autoComplete="cc-exp"
            maxLength={5}
            style={{ color: bodyCopy, ...MONO_INPUT }}
            value={expiry}
            onChange={(e) => setExpiry(formatCardExpiry(e.target.value))}
          />
        </FloatingField>
      </div>

      <Button
        type="submit"
        size="large"
        disabled={!valid}
        background={backgroundBtn}
        color={textColorBtn}
      >
        {t("common.continue")}
      </Button>
    </form>
  );
};

const sanitizeDigits = (raw: string, length?: number): string => {
  const digits = raw.replace(/\D/g, "");
  return length != null ? digits.slice(0, length) : digits;
};

const sanitizeAccountNumber = (raw: string, exactLength?: number): string => {
  if (exactLength != null) return sanitizeDigits(raw, exactLength);
  return raw
    .replace(/\s/g, "")
    .replace(/[^a-z0-9]/gi, "")
    .toUpperCase()
    .slice(0, MAX_ACCOUNT_NUMBER_LENGTH);
};

interface SubFormProps {
  destinationCountry: string;
  options?: EstimateOption[];
  value?: FundRequestPayoutInput | null;
  onSubmit: (payout: FundRequestPayoutInput) => void;
}

interface CashPickupFormProps extends SubFormProps {
  habitsBusy?: boolean;
  habitsEmpty?: boolean;
}

const BankAccountForm: React.FC<SubFormProps> = ({
  destinationCountry,
  options = [],
  value,
  onSubmit,
}) => {
  const { t } = useTranslation("request");
  const { theme } = useStore();
  const { backgroundBtn, textColorBtn, bodyCopy, errorColor } = theme.whiteView;
  const fieldColors = payoutFieldColors(theme.whiteView);
  const catalogBanks = mockCatalogFor(destinationCountry).banks;

  const methodOptions = useMemo(
    () => options.filter((o) => o.payoutMethod === "BANK_ACCOUNT"),
    [options],
  );
  const offerablePayerCodes = useMemo(() => {
    const set = new Set<string>();
    for (const o of methodOptions) {
      if (o.payerCode) set.add(o.payerCode);
    }
    return set;
  }, [methodOptions]);

  const banks = useMemo(() => {
    if (methodOptions.length === 0) return catalogBanks;
    if (offerablePayerCodes.size === 0) return catalogBanks;
    return catalogBanks.filter((b) =>
      [...offerablePayerCodes].some((code) => payerCodesEquivalent(code, b.id)),
    );
  }, [catalogBanks, methodOptions, offerablePayerCodes]);

  const [bankId, setBankId] = useState<string | null>(value?.bankId ?? null);
  const [accountNumber, setAccountNumber] = useState<string>(
    value?.accountNumber ?? "",
  );
  const expectedLength = ACCOUNT_LENGTH[destinationCountry.toUpperCase()];
  const identifier = sanitizeAccountNumber(accountNumber, expectedLength);
  const inspection = inspectBankAccountNumber(destinationCountry, identifier);
  const selectedPrefixes = banks.find((b) => b.id === bankId)?.accountPrefixes;
  const mismatch =
    inspection.canContinue &&
    bankPrefixMismatch(inspection.bankCode, selectedPrefixes);
  const valid =
    Boolean(bankId) &&
    inspection.canContinue &&
    banks.some((b) => b.id === bankId);

  return (
    <form
      className="payout-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || !bankId) return;
        onSubmit({ bankId, accountNumber: identifier });
      }}
    >
      <div className="payout-field">
        <FloatingField
          label={t("payout.fields.bank")}
          value={bankId ?? ""}
          colors={fieldColors}
        >
          <select
            style={{ color: bodyCopy, cursor: "pointer" }}
            value={bankId ?? ""}
            onChange={(e) => {
              setBankId(e.target.value || null);
            }}
          >
            <option value="" />
            {banks.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </FloatingField>
        {banks.length === 0 && (
          <span className="payout-hint">{t("payout.fields.noBanks")}</span>
        )}
      </div>

      <div className="payout-field">
        <FloatingField
          label={t("payout.fields.accountNumber")}
          value={identifier}
          colors={fieldColors}
        >
          <input
            type="text"
            inputMode={expectedLength == null ? "text" : "numeric"}
            maxLength={expectedLength ?? MAX_ACCOUNT_NUMBER_LENGTH}
            autoComplete="off"
            style={{ color: bodyCopy, ...MONO_INPUT }}
            value={identifier}
            onChange={(e) => {
              setAccountNumber(
                sanitizeAccountNumber(e.target.value, expectedLength),
              );
            }}
          />
        </FloatingField>
        {expectedLength != null && (
          <span className="payout-hint">
            {identifier.length} / {expectedLength}
          </span>
        )}
        {identifier.length > 0 && !inspection.formatOk && (
          <span className="payout-hint" style={{ color: errorColor }}>
            {t("payout.fields.accountNumberInvalid")}
          </span>
        )}
        {mismatch && (
          <span className="payout-hint" style={{ color: errorColor }}>
            {t("payout.fields.bankMismatch")}
          </span>
        )}
      </div>

      <Button
        type="submit"
        size="large"
        disabled={!valid}
        background={backgroundBtn}
        color={textColorBtn}
      >
        {mismatch
          ? t("payout.fields.bankMismatchConfirm")
          : t("common.continue")}
      </Button>
    </form>
  );
};

const CashPickupForm: React.FC<CashPickupFormProps> = ({
  destinationCountry,
  options = [],
  value,
  habitsBusy = false,
  habitsEmpty = false,
  onSubmit,
}) => {
  const { t } = useTranslation("request");
  const { theme } = useStore();
  const { backgroundBtn, textColorBtn, bodyCopy } = theme.whiteView;
  const fieldColors = payoutFieldColors(theme.whiteView);
  const catalog = mockCatalogFor(destinationCountry);

  const offerablePayerCodes = useMemo(
    () => new Set(dedupeOfferableCashPayers(options)),
    [options],
  );

  const optionsPreferAnyState = useMemo(
    () =>
      options.some(
        (o) =>
          o.payoutMethod === "CASH_PICKUP" &&
          isWildcardStateCapability(o.capabilityKey),
      ),
    [options],
  );

  const isAnyStateMode =
    optionsPreferAnyState ||
    (catalog.cashStates.length === 0 && offerablePayerCodes.size > 0);

  const savedHabits = useMemo(() => {
    if (habitsEmpty) return [];
    return requestCashPickupHabits.filter((h) =>
      [...offerablePayerCodes].some((code) =>
        payerCodesEquivalent(code, h.payerId),
      ),
    );
  }, [offerablePayerCodes, habitsEmpty]);

  const [stateCode, setStateCode] = useState<string | null>(
    value?.stateCode ?? null,
  );
  const [payerId, setPayerId] = useState<string | null>(value?.payerId ?? null);

  const offerablePayerCodesForState = useMemo(
    () => new Set(dedupeOfferableCashPayersForState(options, stateCode)),
    [options, stateCode],
  );

  const stateOptions = useMemo(() => {
    const all = catalog.cashStates;
    if (isAnyStateMode || offerablePayerCodes.size === 0) return all;
    return all.filter(
      (s) => dedupeOfferableCashPayersForState(options, s.code).length > 0,
    );
  }, [
    catalog.cashStates,
    isAnyStateMode,
    offerablePayerCodes.size,
    options,
  ]);

  useEffect(() => {
    if (!stateCode) return;
    if (stateOptions.length === 0) return;
    const stillOffered = stateOptions.some(
      (s) => s.code.toUpperCase() === stateCode.toUpperCase(),
    );
    if (!stillOffered) {
      setStateCode(null);
      setPayerId(null);
    }
  }, [stateOptions, stateCode]);

  const payerList = useMemo(() => {
    if (!stateCode) return [];
    const fromQuote = [...offerablePayerCodesForState];
    if (fromQuote.length > 0) {
      return fromQuote
        .map((code) => {
          const name = catalogNamedPayer(code, catalog.cashPayers);
          return name ? { id: code, name } : null;
        })
        .filter((row): row is { id: string; name: string } => row != null)
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    if (offerablePayerCodes.size === 0) {
      return catalog.cashPayers.filter((p) => p.name?.trim());
    }
    return catalog.cashPayers.filter(
      (p) =>
        Boolean(p.name?.trim()) &&
        [...offerablePayerCodes].some((code) =>
          payerCodesEquivalent(code, p.id),
        ),
    );
  }, [
    stateCode,
    offerablePayerCodesForState,
    catalog.cashPayers,
    offerablePayerCodes,
  ]);

  const effectivePayerId = useMemo(() => {
    if (!payerId) return null;
    const exact = payerList.find((p) => p.id === payerId);
    if (exact) return exact.id;
    const aliased = payerList.find((p) => payerCodesEquivalent(p.id, payerId));
    return aliased?.id ?? null;
  }, [payerId, payerList]);

  const canResolveSku = Boolean(
    stateCode &&
      effectivePayerId &&
      resolveCashPickupOption(options, effectivePayerId, stateCode),
  );

  const valid = canResolveSku;

  const labeledHabits = savedHabits.filter(
    (h) =>
      Boolean(catalogNamedPayer(h.payerId, catalog.cashPayers)) &&
      Boolean(h.stateName),
  );
  const showSavedBlock = habitsBusy || labeledHabits.length > 0;

  const selectHabit = (habit: (typeof requestCashPickupHabits)[number]) => {
    onSubmit({
      stateCode: habit.stateCode,
      payerId: habit.payerId,
      source: "saved",
    });
  };

  return (
    <form
      className="payout-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || !stateCode || !effectivePayerId) return;
        onSubmit({ stateCode, payerId: effectivePayerId });
      }}
    >
      {showSavedBlock && (
        <section
          className="payout-saved"
          aria-label={t("payout.savedDestinations.aria")}
          aria-busy={habitsBusy}
        >
          <span className="payout-field-label">
            {t("payout.savedDestinations.label")}
          </span>
          <div role="list" className="payout-saved-list">
            {habitsBusy ? (
              <>
                {Array.from({ length: 2 }, (_, i) => (
                  <div
                    key={i}
                    className="payout-saved-skeleton"
                    aria-hidden="true"
                  >
                    <span className="payout-saved-skeleton-line" />
                    <span className="payout-saved-skeleton-line short" />
                  </div>
                ))}
              </>
            ) : (
              labeledHabits.map((habit) => (
                <button
                  key={`${habit.stateCode}:${habit.payerId}`}
                  type="button"
                  role="listitem"
                  className="payout-saved-item"
                  onClick={() => selectHabit(habit)}
                >
                  <span className="payout-saved-title">
                    {catalogNamedPayer(habit.payerId, catalog.cashPayers)}
                  </span>
                  <span className="payout-hint" style={{ color: bodyCopy }}>
                    {habit.stateName}
                  </span>
                </button>
              ))
            )}
          </div>
          <span className="payout-hint payout-or-new">
            {t("payout.savedDestinations.orNew")}
          </span>
        </section>
      )}

      <div className="payout-field">
        <FloatingField
          label={t("payout.fields.state")}
          value={stateCode ?? ""}
          colors={fieldColors}
        >
          <select
            style={{ color: bodyCopy, cursor: "pointer" }}
            value={stateCode ?? ""}
            onChange={(e) => {
              setStateCode(e.target.value || null);
              setPayerId(null);
            }}
          >
            <option value="" />
            {stateOptions.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name}
              </option>
            ))}
          </select>
        </FloatingField>
        {stateOptions.length === 0 && (
          <span className="payout-hint">{t("payout.fields.noStates")}</span>
        )}
      </div>

      {stateCode && (
        <div className="payout-field">
          <FloatingField
            label={t("payout.fields.payer")}
            value={effectivePayerId ?? ""}
            colors={fieldColors}
          >
            <select
              style={{ color: bodyCopy, cursor: "pointer" }}
              value={effectivePayerId ?? ""}
              onChange={(e) => setPayerId(e.target.value || null)}
            >
              <option value="" />
              {payerList.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </FloatingField>
          {payerList.length === 0 && (
            <span className="payout-hint">{t("payout.fields.noPayers")}</span>
          )}
        </div>
      )}

      <Button
        type="submit"
        size="large"
        disabled={!valid}
        background={backgroundBtn}
        color={textColorBtn}
      >
        {t("common.continue")}
      </Button>
    </form>
  );
};

const MobileWalletForm: React.FC<SubFormProps> = ({
  destinationCountry,
  value,
  onSubmit,
}) => {
  const { t } = useTranslation("request");
  const { theme } = useStore();
  const { backgroundBtn, textColorBtn, bodyCopy } = theme.whiteView;
  const fieldColors = payoutFieldColors(theme.whiteView);
  const carriers = mockCatalogFor(destinationCountry).carriers;

  const [carrierId, setCarrierId] = useState<string | null>(
    value?.carrierId ?? null,
  );
  const [phone, setPhone] = useState(value?.phone ?? "");

  const valid = Boolean(carrierId) && phone.replace(/\D/g, "").length >= 8;

  return (
    <form
      className="payout-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid || !carrierId) return;
        onSubmit({ carrierId, phone });
      }}
    >
      <div className="payout-field">
        <FloatingField
          label={t("payout.fields.carrier")}
          value={carrierId ?? ""}
          colors={fieldColors}
        >
          <select
            style={{ color: bodyCopy, cursor: "pointer" }}
            value={carrierId ?? ""}
            onChange={(e) => setCarrierId(e.target.value || null)}
          >
            <option value="" />
            {carriers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </FloatingField>
      </div>

      <div className="payout-field">
        <FloatingField
          label={t("payout.fields.phone")}
          value={phone}
          colors={fieldColors}
        >
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            style={{ color: bodyCopy, ...MONO_INPUT }}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </FloatingField>
      </div>

      <Button
        type="submit"
        size="large"
        disabled={!valid}
        background={backgroundBtn}
        color={textColorBtn}
      >
        {t("common.continue")}
      </Button>
    </form>
  );
};
